import React from 'react';
import { Helmet } from 'react-helmet-async';

// SEO component for consistent meta tags and structured data
const SEO = ({
  title,
  description,
  keywords,
  author = "Genzix Team",
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = "website",
  twitterCard = "summary_large_image",
  twitterTitle,
  twitterDescription,
  twitterImage,
  structuredData,
  noindex = false,
  nofollow = false,
  article = false,
  publishedTime,
  modifiedTime,
  section,
  tags = [],
  breadcrumbs = [],
  faqData = [],
  serviceData = null,
  projectData = null
}) => {
  const siteTitle = "Genzix - Software Development Company";
  const siteDescription = "Leading software development company specializing in web development, AI automation, cloud infrastructure, and digital transformation solutions.";
  const siteUrl = "https://genzix.in";
  
  const fullTitle = title
    ? (title.includes('Genzix') ? title : `${title} | Genzix`)
    : siteTitle;
  const fullDescription = description || siteDescription;
  const fullCanonicalUrl = canonicalUrl || `${siteUrl}${window.location.pathname}`;
  
  const robotsContent = [];
  if (noindex) robotsContent.push('noindex');
  if (nofollow) robotsContent.push('nofollow');
  if (robotsContent.length === 0) robotsContent.push('index', 'follow');
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={fullDescription} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content={robotsContent.join(', ')} />
      <link rel="canonical" href={fullCanonicalUrl} />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={ogTitle || fullTitle} />
      <meta property="og:description" content={ogDescription || fullDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:site_name" content="Genzix" />
      <meta property="og:image" content={ogImage || `${siteUrl}/logo.png`} />
      <meta property="og:image:alt" content={ogTitle || fullTitle} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={twitterTitle || fullTitle} />
      <meta name="twitter:description" content={twitterDescription || fullDescription} />
      <meta name="twitter:image" content={twitterImage || ogImage || `${siteUrl}/logo.png`} />
      <meta name="twitter:site" content="@genzix" />
      <meta name="twitter:creator" content="@genzix" />
      
      {/* Article-specific Meta Tags */}
      {article && (
        <>
          <meta name="article:section" content={section} />
          {publishedTime && <meta name="article:published_time" content={publishedTime} />}
          {modifiedTime && <meta name="article:modified_time" content={modifiedTime} />}
          <meta name="article:author" content={author} />
          {tags.map((tag, index) => (
            <meta key={index} name="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Additional SEO Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#667eea" />
      <meta name="msapplication-TileColor" content="#667eea" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Genzix" />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* Breadcrumb Structured Data */}
      {breadcrumbs.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbs.map((crumb, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "name": crumb.name,
              "item": crumb.url
            }))
          })}
        </script>
      )}
      
      {/* FAQ Structured Data */}
      {faqData.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqData.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>
      )}
      
      {/* Service Structured Data */}
      {serviceData && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": serviceData.name,
            "description": serviceData.description,
            "provider": {
              "@type": "Organization",
              "name": "Genzix",
              "url": "https://genzix.in"
            },
            "areaServed": {
              "@type": "Country",
              "name": "Worldwide"
            },
            "serviceType": serviceData.category,
            "offers": {
              "@type": "Offer",
              "description": serviceData.description
            }
          })}
        </script>
      )}
      
      {/* Project/Software Application Structured Data */}
      {projectData && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": projectData.name,
            "description": projectData.description,
            "applicationCategory": projectData.category,
            "operatingSystem": projectData.platforms,
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "author": {
              "@type": "Organization",
              "name": "Genzix",
              "url": "https://genzix.in"
            },
            "datePublished": projectData.publishedDate,
            "dateModified": projectData.lastModified
          })}
        </script>
      )}
      
      {/* Local Business Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Genzix",
          "description": "Leading software development company specializing in web development, AI automation, cloud infrastructure, and digital transformation solutions.",
          "url": "https://genzix.in",
          "telephone": "+91-9381048755",
          "email": "teamgenzix@gmail.com",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "IN",
            "addressRegion": "India"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "12.9716",
            "longitude": "77.5946"
          },
          "openingHours": "Mo-Fr 09:00-18:00",
          "priceRange": "$$",
          "paymentAccepted": "Cash, Credit Card, Bank Transfer",
          "currenciesAccepted": "INR, USD",
          "areaServed": {
            "@type": "Country",
            "name": "Worldwide"
          }
        })}
      </script>
      
      {/* Professional Service Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "name": "Genzix Software Development Services",
          "description": "Professional software development services including web development, mobile app development, AI automation, and cloud infrastructure solutions.",
          "url": "https://genzix.in",
          "telephone": "+91-9381048755",
          "email": "teamgenzix@gmail.com",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "IN",
            "addressRegion": "India"
          },
          "areaServed": {
            "@type": "Country",
            "name": "Worldwide"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Software Development Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Web Development",
                  "description": "Custom web applications and websites built with modern technologies"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Mobile App Development",
                  "description": "Native and cross-platform mobile applications for iOS and Android"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "AI Automation",
                  "description": "Artificial intelligence solutions and automation services"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Cloud Infrastructure",
                  "description": "Cloud migration, setup, and management services"
                }
              }
            ]
          }
        })}
      </script>
      
      {/* Preconnect to external domains for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* Favicon and Icons */}
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" sizes="180x180" href="/logo192.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/logo192.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/logo192.png" />
      <link rel="manifest" href="/manifest.json" />
    </Helmet>
  );
};

export default SEO;
